import { useState } from 'react';

function ConfidenceDot({ value }) {
  const cls = value >= 0.9 ? 'confidence-dot-high' : value >= 0.75 ? 'confidence-dot-medium' : 'confidence-dot-low';
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} title={`${Math.round(value * 100)}%`} />;
}

export function JobDataTabs({ job, onReview }) {
  const [activeTab, setActiveTab] = useState('spaces');

  const tabs = [
    { key: 'spaces', label: 'Spaces', count: job.spaces?.length || 0 },
    { key: 'assets', label: 'Asset Candidates', count: job.asset_candidates?.length || 0 },
    { key: 'schedules', label: 'Schedule Rows', count: job.schedule_rows?.length || 0 },
    { key: 'sheets', label: 'Sheets', count: job.sheets?.length || 0 },
  ];

  return (
    <>
      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`px-4 py-2.5 font-heading text-xs uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label} <span className="font-mono ml-1 text-[10px]">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border rounded-sm">
        {activeTab === 'spaces' && (
          <div>
            <div className="grid grid-cols-[40px_1fr_1fr_80px_1fr_80px_80px_120px] gap-2 px-4 py-2.5 border-b border-border text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              <span></span><span>Room #</span><span>Name</span><span>Level</span><span>Department</span><span>Area</span><span>Conf</span><span>Action</span>
            </div>
            <div className="divide-y divide-slate-800/30 max-h-[400px] overflow-auto">
              {(job.spaces || []).map((space) => (
                <div key={space.id} className="grid grid-cols-[40px_1fr_1fr_80px_1fr_80px_80px_120px] gap-2 px-4 py-2 items-center text-sm hover:bg-secondary/20 transition-colors" data-testid={`space-row-${space.id}`}>
                  <ConfidenceDot value={space.confidence} />
                  <span className="font-mono text-xs text-foreground">{space.room_number}</span>
                  <span className="font-mono text-xs text-foreground truncate">{space.room_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{space.level_key}</span>
                  <span className="font-mono text-xs text-muted-foreground truncate">{space.department || '-'}</span>
                  <span className="font-mono text-xs text-muted-foreground">{space.area ? `${space.area} sf` : '-'}</span>
                  <span className={`font-mono text-[10px] ${space.confidence >= 0.9 ? 'text-emerald-400' : space.confidence >= 0.75 ? 'text-amber-400' : 'text-rose-400'}`}>
                    {Math.round(space.confidence * 100)}%
                  </span>
                  <div className="flex gap-1">
                    {space.status === 'pending' ? (
                      <>
                        <button onClick={() => onReview('space', space.id, 'approved')} className="px-2 py-0.5 text-[10px] font-mono bg-emerald-950/30 text-emerald-400 border border-emerald-900 rounded hover:bg-emerald-950/50 transition-colors" data-testid={`approve-space-${space.id}`}>OK</button>
                        <button onClick={() => onReview('space', space.id, 'rejected')} className="px-2 py-0.5 text-[10px] font-mono bg-rose-950/30 text-rose-400 border border-rose-900 rounded hover:bg-rose-950/50 transition-colors" data-testid={`reject-space-${space.id}`}>REJ</button>
                      </>
                    ) : (
                      <span className={`text-[10px] font-mono uppercase ${space.status === 'approved' ? 'text-emerald-400' : 'text-rose-400'}`}>{space.status}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'assets' && (
          <div>
            <div className="grid grid-cols-[40px_1fr_1fr_80px_1fr_1fr_1fr_80px_120px] gap-2 px-4 py-2.5 border-b border-border text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              <span></span><span>Tag</span><span>Type</span><span>Room</span><span>Method</span><span>Mfr</span><span>Model</span><span>Conf</span><span>Action</span>
            </div>
            <div className="divide-y divide-slate-800/30 max-h-[400px] overflow-auto">
              {(job.asset_candidates || []).map((asset) => (
                <div key={asset.id} className="grid grid-cols-[40px_1fr_1fr_80px_1fr_1fr_1fr_80px_120px] gap-2 px-4 py-2 items-center text-sm hover:bg-secondary/20 transition-colors" data-testid={`asset-row-${asset.id}`}>
                  <ConfidenceDot value={asset.confidence} />
                  <span className="font-mono text-xs text-foreground font-medium">{asset.tag}</span>
                  <span className="font-mono text-xs text-foreground truncate">{asset.asset_type}</span>
                  <span className="font-mono text-xs text-muted-foreground">{asset.room_number || '-'}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{asset.extraction_method}</span>
                  <span className="font-mono text-[10px] text-muted-foreground truncate" data-testid={`asset-mfr-${asset.id}`}>{asset.manufacturer || '-'}</span>
                  <span className="font-mono text-[10px] text-muted-foreground truncate" data-testid={`asset-model-${asset.id}`}>{asset.model_info || '-'}</span>
                  <span className={`font-mono text-[10px] ${asset.confidence >= 0.9 ? 'text-emerald-400' : asset.confidence >= 0.75 ? 'text-amber-400' : 'text-rose-400'}`}>
                    {Math.round(asset.confidence * 100)}%
                  </span>
                  <div className="flex gap-1">
                    {asset.status === 'pending' ? (
                      <>
                        <button onClick={() => onReview('asset_candidate', asset.id, 'approved')} className="px-2 py-0.5 text-[10px] font-mono bg-emerald-950/30 text-emerald-400 border border-emerald-900 rounded hover:bg-emerald-950/50 transition-colors" data-testid={`approve-asset-${asset.id}`}>OK</button>
                        <button onClick={() => onReview('asset_candidate', asset.id, 'rejected')} className="px-2 py-0.5 text-[10px] font-mono bg-rose-950/30 text-rose-400 border border-rose-900 rounded hover:bg-rose-950/50 transition-colors" data-testid={`reject-asset-${asset.id}`}>REJ</button>
                      </>
                    ) : (
                      <span className={`text-[10px] font-mono uppercase ${asset.status === 'approved' ? 'text-emerald-400' : 'text-rose-400'}`}>{asset.status}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'schedules' && (
          <div>
            <div className="grid grid-cols-[40px_100px_2fr_2fr_80px] gap-2 px-4 py-2.5 border-b border-border text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              <span></span><span>Type</span><span>Raw Data</span><span>Normalized</span><span>Conf</span>
            </div>
            <div className="divide-y divide-slate-800/30 max-h-[400px] overflow-auto">
              {(job.schedule_rows || []).map((row) => (
                <div key={row.id} className="grid grid-cols-[40px_100px_2fr_2fr_80px] gap-2 px-4 py-2 items-center hover:bg-secondary/20 transition-colors" data-testid={`schedule-row-${row.id}`}>
                  <ConfidenceDot value={row.confidence} />
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide border w-fit ${row.schedule_type === 'room' ? 'bg-blue-950/50 text-blue-400 border-blue-900' : 'bg-violet-950/50 text-violet-400 border-violet-900'}`}>
                    {row.schedule_type}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground truncate">{JSON.stringify(row.row_data).substring(0, 80)}...</span>
                  <span className="font-mono text-[10px] text-foreground truncate">{JSON.stringify(row.normalized_fields).substring(0, 80)}...</span>
                  <span className={`font-mono text-[10px] ${row.confidence >= 0.9 ? 'text-emerald-400' : row.confidence >= 0.75 ? 'text-amber-400' : 'text-rose-400'}`}>
                    {Math.round(row.confidence * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'sheets' && (
          <div>
            <div className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr] gap-2 px-4 py-2.5 border-b border-border text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              <span>Sheet #</span><span>Name</span><span>Discipline</span><span>Scale</span><span>File</span>
            </div>
            <div className="divide-y divide-slate-800/30 max-h-[400px] overflow-auto">
              {(job.sheets || []).map((sheet) => (
                <div key={sheet.id} className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr] gap-2 px-4 py-2.5 items-center hover:bg-secondary/20 transition-colors" data-testid={`sheet-row-${sheet.id}`}>
                  <span className="font-mono text-xs text-primary font-medium">{sheet.sheet_number}</span>
                  <span className="font-mono text-xs text-foreground">{sheet.sheet_name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{sheet.discipline}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{sheet.scale_hint || '-'}</span>
                  <span className="font-mono text-[10px] text-muted-foreground truncate">{sheet.file_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
